/*
 * Zoom-from-Origin v3.4.3
 * ---------------------------------
 *
 * v3.4.3 changes (additions):
 * Fixed prev / next for loopMode 'bounded'
 * Added change fabric picker selected to match jonnzoom active slide 
 * so when zooms back in same image origin of jonny zoom matches fabric picker image
 *
 * This file starts from your v3.4.1b source (comments preserved) and applies
 * small, clearly marked patches for:
 *
 * v3.4.1c changes (additions vs v3.4.1b):
 * 1) Config-only initialization:
 *    - You can now call: jonnyZoom({ triggerSelector: '.jonnyzoom' })
 *      (or use legacy name targetSelector).
 *    - Internally, when only a config object is passed, the gallery root
 *      falls back to `document`.
 *
 * 2) Parent/root optional:
 *    - If no rootSelector is provided or it's invalid, we bind click handlers
 *      on `document` (no throw).
 *
 * 3) triggerSelector vs targetSelector:
 *    - `config.triggerSelector` is the primary selector for triggers.
 *    - `config.targetSelector` remains as a backward-compatible alias.
 *    - All queries that used targetSelector in `.open()` now use triggerSelector.
 *
 * 4) `.open()` behavior:
 *    - `.open(number)` looks up triggers via
 *      `(config.parentSelector ? that node : (gallery || document))`.
 *    - `.open(string)` queries `(gallery || document)`.
 *
 * 5) (NEW in 3.4.1c) Image-from-href support when no child <img>:
 *    - If an <a> has no <img> child but its href is an image URL,
 *      the item opens using that href (with a fade/scale, like HTML mode).
 *
 * Everything else remains identical to v3.4.1b.
 *
 * ---------------------------------
 *
 * This block is the v3.4.0 script with comments preserved, plus the
 * v3.4.1 HTML-mode additions clearly commented.
 *
 * Changes from v3.4.1:
 * - Added HTML mode: anchors whose href begins with '#' open a cloned
 *   HTML panel inside the modal (no zoom-from-origin animation).
 * - .jonnyzoom-modal-html container is injected; hidden/display:none are removed
 *   on the cloned root to ensure visibility in the modal.
 * - Optional sizing controls via data-html-w/data-html-h or
 *   data-html-aspect (default 4:3).
 * - API: instance.open(<anchor>) works for hash-href anchors too.
 * - Close flow for HTML mode is a fade/scale rather than reverse zoom.
 *
 * (Everything below keeps v3.4.0 behavior for images/video/YouTube.)
 *
 * ---------------- (v3.4.0 header, kept) ----------------
 * Changes from v3.4.0
 *
 * Added .open() method to gallery instance
 * - Allows programmatic opening of a zoom item without user click.
 * - Accepted argument types:
 *     1. Number → index of <a.jonnyzoom> in the gallery (0-based).
 *     2. String → CSS selector (relative to the gallery) for the target element.
 *     3. Element → direct DOM reference to <img> or <a.jonnyzoom>.
 * - Behavior:
 *     • If target is an <a.jonnyzoom>, finds its <img> and opens it.
 *      • If target is an <img>, opens it via its closest <a.jonnyzoom> or itself.
 *     • Logs warnings for out-of-range indexes, missing elements, or unsupported targets.
 * - Written with clear, separate if-blocks for readability (no else-if chains).
 *
 *
 * Changes from v3.3.9a:
 *
 * 1. Added guard in wait() so it only runs when rootSelector is a string.
 * 2. jonnyZoom now always returns a Promise.
 * 3. Added global name collision warning before attaching to window.
 * 4. Throws explicit errors instead of returning {} on init failure.
 * 5. Tracks a single onGalleryClick reference for add/removeEventListener to prevent memory leaks.
 * 6. Exposed global instance is now Object.freeze() to avoid accidental mutation.
 *
 * Changes vs 3.3.9:
 * - Only call wait() when rootSelector is a string
 * - jonnyZoom returns a Promise (no fire-and-forget IIFE)
 * - Warn on duplicate globalName collisions
 * - Throw on invalid root (no silent `{}`), fail fast
 * - Store/remove one onGalleryClick handler (leak fix)
 * - Freeze exposed global instance to reduce accidental mutation
 * - Keeps: removed pointerdown for thumbnails; pointer-events only active media; async helpers
 */

(function (global) {
  'use strict';

  ////////////////////////////////////////////////////////////////
  // wait() — resolves once selector exists in the DOM
  function wait(selector, disconnect) {
    return new Promise(resolve => {
      if (document.querySelector(selector)) return resolve(document.querySelector(selector));
      const observer = new MutationObserver(() => {
        const el = document.querySelector(selector);
        if (el) {
          resolve(el);
          if (disconnect === true) observer.disconnect();
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  /**
   * Create a new zoom‑from‑origin gallery instance bound to a root element.
   * @param {string|Element|Object} rootSelector CSS selector or DOM element
   *        (v3.4.1c: or a CONFIG OBJECT when using config-only init)
   * @param {Object} [userConfig]
   * @returns {Promise<Object>} resolves with the gallery instance API
   */
  function createZoomFromOriginGallery(rootSelector, userConfig = {}) {

    // v3.4.1c — PATCH: allow config-only call:
    // createZoomFromOriginGallery({ triggerSelector: '.foo' })
    // If first arg is a plain object (and not an Element), treat it as the config.
    if (rootSelector && typeof rootSelector === 'object' && !(rootSelector instanceof Element)) {
      userConfig = rootSelector;
      rootSelector = document; // no parent → use document as the gallery root
    }

    // Only wait when a selector string is provided; else use the element as-is
    const ensureRoot = (typeof rootSelector === 'string')
      ? wait(rootSelector, true).then(() => rootSelector)
      : Promise.resolve(rootSelector);

    return ensureRoot.then(() => {
      // Defaults
      const defaultConfig = {
        loopMode: 'bounded',        // 'wrap' | 'bounded'
        navOnTouch: false,       // show nav buttons on touch devices
        thumbs: 'off',            // 'on' | 'off'
        thumbsOnTouchHide: false, // true | false
        autoplay: false,         // autoplay HTML5/YouTube after morph
        // v3.4.1b additions:
        nav: 'off',               // 'on' | 'off' (force-hide prev/next)
        caption: 'on',           // 'on' | 'off' (force-hide caption)
        // v3.4.1b (your earlier addition) + kept in 3.4.1c:
        triggerSelector: '.jonnyzoom'
        //triggerSelector: 'a.jonnyzoom'
      };
      const config = Object.assign({}, defaultConfig, userConfig);

      // v3.4.1c — PATCH: back-compat alias
      // If a user passes targetSelector, honor it (but store in triggerSelector).
      if (!config.triggerSelector && config.targetSelector) {
        config.triggerSelector = config.targetSelector;
      }

      // Resolve gallery element from selector or element
      let gallery;
      if (typeof rootSelector === 'string') {
        if (typeof global.$q === 'function') {
          try {
            const qRes = global.$q(rootSelector);
            if (qRes) {
              if (qRes instanceof Element) gallery = qRes;
              else if (Array.isArray(qRes)) gallery = qRes[0];
              else if (typeof qRes.length === 'number' && qRes.length > 0) gallery = qRes[0];
            }
          } catch (_) { /* fall back */ }
        }
        if (!gallery) gallery = document.querySelector(rootSelector);
      } else {
        gallery = rootSelector;
      }

      // v3.4.1c — PATCH: no-parent mode
      // Instead of throwing, safely fall back to `document` (bind globally).
      if (!gallery || typeof gallery.addEventListener !== 'function') {
        gallery = document;
      }

      // -------- Instance state --------
      let overlay, modal, wrapper, btnClose, captionEl, navWrap, btnPrev, btnNext, thumbsBox;
      let active = null;
      let navBusy = false;
      let tapGuardUntil = 0;
      let thumbDragGuard = 0;

      /* ---------- Touch overrides ---------- */
      (function markTouchAndOverrides(){
        const root = document.documentElement;
        const coarse = global.matchMedia?.('(pointer: coarse)').matches;
        const touchPoints = navigator.maxTouchPoints || navigator.msMaxTouchPoints || 0;
        if (coarse || touchPoints > 0) root.classList.add('is-touch');
        global.addEventListener('pointerdown', (e) => {
          if (e.pointerType === 'touch') root.classList.add('is-touch');
        }, { passive:true, once:true });

        if (config.navOnTouch) {
          root.classList.add('nav-on-touch');
        }
        if (config.thumbsOnTouchHide == true) {
          root.classList.add('hide-thumbs-on-touch');
        }
      })();

      const supportsNativeAuto = CSS.supports?.('interpolate-size','allow-keywords') || false;

      const scrollLock = (() => {
        let prev;
        return {
          /*lock(){ prev = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; },
          unlock(){ document.documentElement.style.overflow = prev || ''; }*/
          lock(){ prev = document.body.style.overflow; document.body.style.overflow = 'hidden'; },
          unlock(){ document.body.style.overflow = prev || ''; }
        };
      })();

      const thumbsEnabled = () => (config.thumbs !== 'off');
      const isWrapMode    = () => (config.loopMode !== 'bounded');

      // ------- Geometry helpers -------
      function clampFinalSize(nw, nh, baseW, vhMax){
        const ratio = nh / nw; let w = baseW; let h = w * ratio;
        if (h > vhMax) { h = vhMax; w = h / ratio; }
        return { w: Math.round(w), h: Math.round(h) };
      }
      function computeFinalSizeFromIntrinsic(nw, nh){
        const vw = global.innerWidth, vh = global.innerHeight;
        const baseW = vw * (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--target-vw')) / 100);
        const vhMax = vh * (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--max-vh')) / 100);
        return clampFinalSize(nw || 1200, nh || 800, baseW, vhMax);
      }
      function computeFinalSizeForImage(img){
        return computeFinalSizeFromIntrinsic(img.naturalWidth || img.width, img.naturalHeight || img.height);
      }
      function computeFinalSizeForAspect(aspectW = 16, aspectH = 9){
        const vw = global.innerWidth, vh = global.innerHeight;
        const baseW = vw * (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--target-vw')) / 100);
        const vhMax = vh * (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--max-vh')) / 100);
        return clampFinalSize(aspectW, aspectH, baseW, vhMax);
      }
      // v3.4.1: size helper for HTML panels
      function computeFinalSizeForHtml(aEl){
        const w = parseFloat(aEl.dataset.htmlW);
        const h = parseFloat(aEl.dataset.htmlH);
        if (Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0) {
          return computeFinalSizeFromIntrinsic(w, h);
        }
        const asp = (aEl.dataset.htmlAspect || '4:3').split(':');
        const aw = Number.parseFloat(asp[0]) || 4;
        const ah = Number.parseFloat(asp[1]) || 3;
        return computeFinalSizeForAspect(aw, ah);
      }

      function computeTranslateDelta(rect){
        const startCx = rect.left + rect.width / 2, startCy = rect.top  + rect.height / 2;
        const endCx   = global.innerWidth  / 2,     endCy   = global.innerHeight / 2;
        return { dx: startCx - endCx, dy: startCy - endCy };
      }

      // ------- Utilities -------
      function preloadImage(url){
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = async () => { try { if (typeof img.decode === 'function') { await img.decode(); } } catch(_){} resolve(img); };
          img.onerror = reject; img.src = url;
        });
      }
      function waitForTransition(el, propName, fallbackMs = 900){
        return new Promise(resolve => {
          let done = false;
          const onEnd = (e) => {
            if (propName && e.propertyName !== propName) return;
            if (done) return; done = true;
            el.removeEventListener('transitionend', onEnd);
            resolve();
          };
          el.addEventListener('transitionend', onEnd);
          setTimeout(() => { if (done) return; done = true; el.removeEventListener('transitionend', onEnd); resolve(); }, fallbackMs);
        });
      }
      function getCaptionForAnchor(aEl, img){
        return aEl.getAttribute('data-caption') || aEl.getAttribute('title') || (img && img.getAttribute('alt')) || '';
      }

      function buildGroup(aEl){
        const groupName = aEl.getAttribute('data-group');
        if (!groupName) return { group: null, els: [aEl], index: 0 };
        const els = Array.from(
          // v3.4.1c — tiny safety: if gallery is somehow null, use document.
          (gallery || document).querySelectorAll(`${config.triggerSelector}[data-group="${CSS.escape(groupName)}"]`)
        );
        const index = Math.max(0, els.indexOf(aEl));
        return { group: groupName, els, index };
      }

      // v3.4.1: helper → is this anchor an HTML-panel link?
      function isHashHref(aEl){
        const href = (aEl.getAttribute('href') || '').trim();
        return href.startsWith('#') && href.length > 1;
      }

      // v3.4.1c — helper → does an href look like an image URL?
      function isImageHref(href) {
        if (!href) return false;
        if (/^data:image\//i.test(href)) return true;
        return /\.(png|jpe?g|webp|gif|bmp|svg)(?:[\?#].*)?$/i.test(href);
      }

      // v3.4.1: build the HTML content clone
      function buildHtmlContent(aEl){
        const href = (aEl.getAttribute('href') || '').trim();
        const id = href.slice(1);
        const src = document.getElementById(id);
        if (!src) return null;
        const clone = src.cloneNode(true);
        if (clone.id) clone.removeAttribute('id');
        clone.removeAttribute('hidden');
        if (clone.style) { clone.style.display = ''; clone.style.visibility = ''; }
        clone.classList.add('jonnyzoom-modal-html');
        return clone;
      }

      // ------- Thumbnails -------
      function renderThumbs(){
        if (!active || !active.groupEls || !thumbsEnabled()) return hideThumbs();
        const count = active.groupEls.length;
        if (count <= 1) return hideThumbs();
        thumbsBox.innerHTML = '';
        thumbsBox.setAttribute('aria-hidden', 'false');
        active.groupEls.forEach((aEl, i) => {
          const t = document.createElement('button');
          t.className = 'jonnyzoom-thumb' + (i === active.groupIndex ? ' is-active' : '');
          t.type = 'button';
          t.dataset.index = String(i);
          const thumbImg = aEl.querySelector('img');
          t.setAttribute('aria-label', (aEl.getAttribute('title') || (thumbImg && thumbImg.alt) || `Image ${i+1}`));
          if (i === active.groupIndex) t.setAttribute('aria-current', 'true');
          const img = thumbImg ? thumbImg.cloneNode(true) : null;
          if (img) {
            img.removeAttribute('sizes');
            img.removeAttribute('srcset');
            img.style.width = '100%';
            img.style.height = '100%';
          }
          t.appendChild(img || document.createTextNode(String(i+1)));
          thumbsBox.appendChild(t);
        });
        attachThumbsDrag(thumbsBox);
        if (!thumbsBox._touchGuardInstalled) { installThumbsTouchGuard(thumbsBox); thumbsBox._touchGuardInstalled = true; }
        thumbsBox.classList.add('is-visible');
      }

      function hideThumbs(){ 
        if (!thumbsBox) return; 
        thumbsBox.classList.remove('is-visible'); 
        thumbsBox.setAttribute('aria-hidden','true'); 
        thumbsBox.innerHTML = ''; 
      }

      function updateThumbActive(newIndex){
        const kids = Array.from(thumbsBox.children);
        kids.forEach((el, i) => {
          if (!(el instanceof HTMLElement)) return;
          if (i === newIndex) {
            el.classList.add('is-active');
            el.setAttribute('aria-current','true');
            try { el.scrollIntoView({ behavior:'smooth', block:'nearest', inline:'center' }); } catch(_) {}
          } else { 
            el.classList.remove('is-active'); 
            el.removeAttribute('aria-current'); 
          }
        });        
        // change fabric picker image slider to same jonnyzoom image slider index
        if(document.querySelector('html.is-builder.modal-visible-drawer #fp-slider') !== null){
            const fpSliderItems = document.querySelectorAll('#fp-slider li');
            document.querySelector('#fp-slider li.selected')?.classList.remove('selected');
            fpSliderItems[newIndex]?.classList.add('selected');
            //console.log('is builder');
        }
      }
      function attachThumbClick(){
        thumbsBox.addEventListener('click', (e) => {
          const btn = e.target.closest('.jonnyzoom-thumb'); if (!btn) return;
          e.stopPropagation(); e.preventDefault();
          if (Date.now() < thumbDragGuard) return;
          const idx = Number(btn.dataset.index);
          if (!Number.isFinite(idx) || !active || navBusy || idx === active.groupIndex) return;
          (async () => { navBusy = true; await showAtIndex(idx); updateNavState(); navBusy = false; })();
        });
      }
      function attachThumbsDrag(container){
        if (container._dragAttached) return; container._dragAttached = true;
        let isDown = false, startX = 0, startLeft = 0;
        const onPointerMove = (e) => { if (!isDown || e.pointerType !== 'mouse') return; const dx = e.clientX - startX; if (dx !== 0) { container.scrollLeft = startLeft - dx; thumbDragGuard = Date.now() + 120; } };
        const onPointerUp = (e) => { if (e.pointerType !== 'mouse') return; isDown = false; container.releasePointerCapture?.(e.pointerId); };
        // Intentionally NOT adding 'pointerdown' so clicks work as expected
        container.addEventListener('pointermove', onPointerMove);
        container.addEventListener('pointerup', onPointerUp);
        container.addEventListener('pointercancel', onPointerUp);
        container.addEventListener('click', (e) => { if (Date.now() < thumbDragGuard) e.stopPropagation(); });
      }
      function installThumbsTouchGuard(box){
        let startX = 0, startY = 0, moved = false, startScrollLeft = 0;
        box.addEventListener('scroll', () => { thumbDragGuard = Date.now() + 180; }, { passive:true });
        box.addEventListener('touchstart', (e) => { const t = e.changedTouches[0]; startX = t.clientX; startY = t.clientY; moved = false; startScrollLeft = box.scrollLeft; }, { passive:true });
        box.addEventListener('touchmove', (e) => { const t = e.changedTouches[0]; const dx = Math.abs(t.clientX - startX); const dy = Math.abs(t.clientY - startY); if (!moved && (dx > 6 || dy > 6)) moved = true; }, { passive:true });
        box.addEventListener('touchend', () => { const didScroll = Math.abs(box.scrollLeft - startScrollLeft) > 2; if (moved || didScroll) thumbDragGuard = Date.now() + 200; }, { passive:true });
      }

      // v3.4.1b: centralized caption visibility gate (respects config.caption)
      function applyCaption(text){
        const show = (config.caption !== 'off') && !!text;
        captionEl.textContent = text || '';
        if (show) {
          captionEl.classList.add('is-visible');
          captionEl.setAttribute('aria-hidden', 'false');
        } else {
          captionEl.classList.remove('is-visible');
          captionEl.setAttribute('aria-hidden', 'true');
        }
      }

      let thumbsRendered = false;

      function ensureThumbsRendered(){
        if (!thumbsRendered) {
          renderThumbs();
          thumbsRendered = true;
        }
      }

      function updateNavState(){
        if (!active || !active.groupEls) return;

        // Force-hide nav if config.nav === 'off'
        if (config.nav === 'off') {
          navWrap.classList.remove('is-visible');
          navWrap.setAttribute('aria-hidden','true');
          //renderThumbs();
          ensureThumbsRendered(); // only render once
          updateThumbActive(active.groupIndex);
          return;
        }

        const root = document.documentElement;
        const touchHide = root.classList.contains('is-touch') && !root.classList.contains('nav-on-touch');
        if (touchHide) {
          navWrap.classList.remove('is-visible');
          navWrap.setAttribute('aria-hidden','true');
        } else {
          const count = active.groupEls.length;
          if (count <= 1) {
            navWrap.classList.remove('is-visible');
            navWrap.setAttribute('aria-hidden','true');
          } else {
            navWrap.classList.add('is-visible');
            navWrap.setAttribute('aria-hidden','false');
            if (isWrapMode()) {
              btnPrev.classList.remove('is-disabled'); 
              btnNext.classList.remove('is-disabled');
              btnPrev.removeAttribute('aria-disabled'); 
              btnNext.removeAttribute('aria-disabled');
              btnPrev.disabled = false; 
              btnNext.disabled = false;
            } else {
              const atStart = active.groupIndex === 0;
              const lastIndex = active.groupEls.length - 1;
              const atEnd = active.groupIndex === lastIndex;
              //console.log(atStart);
              //console.log(atEnd);
              //console.log('active.groupIndex new',active.groupIndex);
              btnPrev.classList.toggle('is-disabled', atStart);
              btnNext.classList.toggle('is-disabled', atEnd);
              btnPrev.setAttribute('aria-disabled', String(atStart));
              btnNext.setAttribute('aria-disabled', String(atEnd));
              btnPrev.disabled = atStart; 
              btnNext.disabled = atEnd;
            }
          }
        }
        //renderThumbs();
        ensureThumbsRendered(); // only render once
        updateThumbActive(active.groupIndex);
      }

      // ------- Modal scaffold -------
      function buildModalScaffold(){
        overlay = document.createElement('div'); overlay.className = 'jonnyzoom-overlay'; overlay.id = 'zoomOverlay_' + Math.random().toString(36).substr(2, 5); overlay.setAttribute('aria-hidden','true');
        modal = document.createElement('div'); modal.className = 'jonnyzoom-modal'; modal.id = 'zoomModal_' + Math.random().toString(36).substr(2, 5); modal.setAttribute('aria-hidden','true');
        btnClose = document.createElement('button'); btnClose.className = 'jonnyzoom-modal-close'; btnClose.type = 'button'; btnClose.setAttribute('aria-label','Close'); btnClose.textContent = 'Close ×';
        wrapper = document.createElement('div'); wrapper.className = 'jonnyzoom-modal-wrapper'; wrapper.id = 'modalWrapper_' + Math.random().toString(36).substr(2, 5);
        captionEl = document.createElement('div'); captionEl.className = 'jonnyzoom-modal-caption'; captionEl.setAttribute('aria-live','polite');
        navWrap = document.createElement('div'); navWrap.className = 'jonnyzoom-modal-nav'; navWrap.setAttribute('aria-hidden','true');
        btnPrev = document.createElement('button'); btnPrev.className = 'prev'; btnPrev.type = 'button'; btnPrev.setAttribute('aria-label','Previous'); btnPrev.textContent = '‹';
        btnNext = document.createElement('button'); btnNext.className = 'next'; btnNext.type = 'button'; btnNext.setAttribute('aria-label','Next'); btnNext.textContent = '›';
        navWrap.appendChild(btnPrev); navWrap.appendChild(btnNext);
        thumbsBox = document.createElement('div'); thumbsBox.className = 'jonnyzoom-modal-thumbs'; thumbsBox.setAttribute('aria-label','Thumbnails'); thumbsBox.setAttribute('aria-hidden','true');
        modal.appendChild(btnClose); modal.appendChild(wrapper); modal.appendChild(captionEl); modal.appendChild(navWrap); modal.appendChild(thumbsBox);
        document.body.appendChild(overlay); document.body.appendChild(modal);
        overlay.addEventListener('click', closeToOrigin);
        btnClose.addEventListener('click', closeToOrigin);
        wrapper.addEventListener('click', (e) => { 
          //console.log(e.target);
          const interactive = e.target.closest('a,button,input,textarea,select,label,[role="button"],[onclick]');
          if (Date.now() < tapGuardUntil && !interactive) return; 
          if (e.target !== wrapper) return; closeToOrigin(); 
        });
        btnPrev.addEventListener('click', (e) => { e.stopPropagation(); if (btnPrev.disabled || btnPrev.classList.contains('is-disabled')) return; navigate(-1); });
        btnNext.addEventListener('click', (e) => { e.stopPropagation(); if (btnNext.disabled || btnNext.classList.contains('is-disabled')) return; navigate(1); });
        global.addEventListener('keydown', handleKeyDown);
        global.addEventListener('resize', handleResize);
        attachThumbClick();
      }
      function destroyModalScaffold(){
        if (!modal || !overlay) return;
        overlay.removeEventListener('click', closeToOrigin);
        btnClose.removeEventListener('click', closeToOrigin);
        wrapper && wrapper.replaceWith(wrapper.cloneNode(true)); // drop wrapper listeners
        global.removeEventListener('keydown', handleKeyDown);
        global.removeEventListener('resize', handleResize);
        overlay.remove();
        modal.remove();
        overlay = modal = wrapper = btnClose = captionEl = navWrap = btnPrev = btnNext = thumbsBox = null;
      }

      function handleKeyDown(e){ if (!active) return; if (e.key === 'Escape') { e.preventDefault(); closeToOrigin(); return; } if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); } if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); } }
      function handleResize(){ if (!active) return; const { frameEl } = active; const prev = frameEl.style.transition; frameEl.style.transition = 'none'; frameEl.style.transform  = 'translate(-50%, -50%) translate(0, 0) scale(1)'; frameEl.getBoundingClientRect(); frameEl.style.transition = prev; }

      // ------- Media builders -------
      function buildHTML5Video(aEl, { autoplay } = { autoplay: false }) {
        const src = aEl.dataset.videoSrc; const poster = aEl.dataset.poster || (aEl.querySelector('img') && aEl.querySelector('img').src) || '';
        if (!src) return null; const v = document.createElement('video'); v.className = 'jonnyzoom-modal-media'; v.controls = true; v.playsInline = true; v.preload = 'metadata'; if (poster) v.poster = poster;
        const source = document.createElement('source'); source.src = src; const ext = src.split('?')[0].split('.').pop().toLowerCase(); if (ext === 'mp4') source.type = 'video/mp4'; v.appendChild(source); v._shouldAutoplay = !!autoplay; return v;
      }

      function buildYouTubeIframe(aEl, { autoplay } = { autoplay: false }) {
        const id = aEl.dataset.youtubeId; if (!id) return null; const url = `https://www.youtube.com/embed/${encodeURIComponent(id)}?autoplay=${autoplay?1:0}&rel=0&modestbranding=1&playsinline=1&enablejsapi=1`;
        const ifr = document.createElement('iframe'); ifr.className = 'jonnyzoom-modal-media'; ifr.src = url; ifr.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture'; ifr.allowFullscreen = true; ifr.loading = 'lazy'; ifr.referrerPolicy = 'strict-origin-when-cross-origin'; return ifr;
      }

      function getMediaType(aEl){
        const t = (aEl.dataset.type || 'image').toLowerCase(); if (t === 'video') return 'video'; if (t === 'youtube') return 'youtube'; /* v3.4.1: treat hash href as html */ if (isHashHref(aEl)) return 'html'; return 'image';
      }

      function ensureActiveMediaVisibility(){
        if (!active || !active.frameEl) return; const frameEl = active.frameEl; if (frameEl.classList.contains('is-morphing')) return;
        const current = (active.mediaType === 'image' || !active.mediaEl) ? active.imgEl : active.mediaEl;
        Array.from(frameEl.children).forEach(child => {
          if (child === current) {
            child.style.opacity = '1';
            child.style.pointerEvents = '';
            // For HTML panels, don’t force 100% sizing:
            if (active?.mediaType !== 'html') {
              child.style.width = '100%';
              child.style.height = '100%';
            } else {
              child.style.width = '';
              child.style.height = '';
            }
          } else {
            child.style.opacity = '0';
            child.style.pointerEvents = 'none';
          }
        });
      }

      // ------- Open / Show / Nav / Close -------

      // v3.4.1: open HTML panel (no zoom-from-origin)
      async function openHtmlFromAnchor(aEl){
        if (active) return; buildModalScaffold();
        const { group, els: groupEls, index: groupIndex } = buildGroup(aEl);
        const captionText = getCaptionForAnchor(aEl, aEl.querySelector('img'));
        applyCaption(captionText);
        const frame = document.createElement('div'); 
        frame.className = 'jonnyzoom-modal-frame';
        const { w: finalW, h: finalH } = computeFinalSizeForHtml(aEl);
        // Use auto so content can size naturally while still clamped by frame styles
        frame.style.width = 'auto';
        frame.style.height = 'auto';
        wrapper.appendChild(frame);
        const htmlNode = buildHtmlContent(aEl);
        if (htmlNode) frame.appendChild(htmlNode);
        modal.classList.add('is-active'); 
        modal.setAttribute('aria-hidden','false');
        // Fade overlay in immediately and scale frame in slightly
        overlay.classList.add('is-active'); 
        overlay.setAttribute('aria-hidden','false'); 
        scrollLock.lock();
        frame.style.transition = 'none';
        frame.style.transform = 'translate(-50%,-50%) scale(0.96)';
        frame.getBoundingClientRect(); 
        frame.style.transition = '';
        requestAnimationFrame(() => { frame.style.transform = 'translate(-50%,-50%) scale(1)'; });
        active = { aEl, sourceImg: null, frameEl: frame, imgEl: null, finalW, finalH, hScaled: finalH, supportsNativeAuto, group, groupEls, groupIndex, captionText, mediaType: 'html', mediaEl: htmlNode };
        // No swipe in HTML mode so inner UI stays fully interactive:
        //attachSwipe(frame); 
        updateNavState(); 
        ensureActiveMediaVisibility();
      }

      // v3.4.1c — NEW: open image directly from href when there is no child <img> (no zoom-from-origin)
      async function openImageHrefNoThumb(aEl){
        if (active) return; buildModalScaffold();

        const { group, els: groupEls, index: groupIndex } = buildGroup(aEl);
        const href = (aEl.getAttribute('href') || '').trim();
        const captionText = getCaptionForAnchor(aEl, null);
        applyCaption(captionText);

        const frame = document.createElement('div'); frame.className = 'jonnyzoom-modal-frame';
        wrapper.appendChild(frame);

        // Preload to get intrinsic size for a nicer initial frame size
        let imgEl;
        try {
          const hi = await preloadImage(href);
          imgEl = hi; // already decoded
        } catch (_) {
          imgEl = new Image();
          imgEl.src = href; // best effort
        }
        imgEl.className = 'jonnyzoom-modal-image';
        imgEl.style.opacity = '0';
        frame.appendChild(imgEl);

        // Size/motion: fade/scale (like HTML mode)
        const { w: finalW, h: finalH } = computeFinalSizeFromIntrinsic(imgEl.naturalWidth, imgEl.naturalHeight);
        //const { w: finalW, h: finalH } = computeFinalSizeFromIntrinsic(imgEl.naturalWidth || 1200, imgEl.naturalHeight || 800);
        //console.log(imgEl.naturalWidth);
        //console.log(imgEl.naturalHeight);
        frame.style.width = finalW + 'px';//imgEl.naturalWidth + 'px';
        frame.style.height = finalH + 'px';//imgEl.naturalWHeight + 'px';

        modal.classList.add('is-active'); 
        modal.setAttribute('aria-hidden','false');
        overlay.classList.add('is-active'); 
        overlay.setAttribute('aria-hidden','false'); 
        scrollLock.lock();

        frame.style.transition = 'none';
        frame.style.transform = 'translate(-50%,-50%) scale(0.96)';
        frame.getBoundingClientRect(); 
        frame.style.transition = '';
        requestAnimationFrame(() => {
          frame.style.transform = 'translate(-50%,-50%) scale(1)';
          imgEl.style.opacity = '1';
        });

        active = {
          aEl,
          sourceImg: null,               // no origin img
          frameEl: frame,
          imgEl,
          finalW,
          finalH,
          hScaled: finalH,
          supportsNativeAuto,
          group,
          groupEls,
          groupIndex,
          captionText,
          mediaType: 'image',
          mediaEl: imgEl
        };
        attachSwipe(frame); 
        updateNavState(); 
        ensureActiveMediaVisibility();
      }

      async function openFromOrigin(aEl, thumbImg){
        if (active) return; buildModalScaffold();
        const thumbRect = thumbImg.getBoundingClientRect(); 
        if (thumbRect.width <= 0 || thumbRect.height <= 0) return;
        const { group, els: groupEls, index: groupIndex } = buildGroup(aEl);
        const captionText = getCaptionForAnchor(aEl, thumbImg);
        applyCaption(captionText);
        const mediaType = getMediaType(aEl);
        const highResUrl = mediaType === 'image' ? (aEl.getAttribute('href') || thumbImg.currentSrc || thumbImg.src) : null;
        const hiResPromise = (highResUrl && highResUrl !== thumbImg.src) ? preloadImage(highResUrl).catch(() => null) : Promise.resolve(null);

        const frame = document.createElement('div'); 
        frame.className = 'jonnyzoom-modal-frame';
        const clone = thumbImg.cloneNode(true); 
        clone.className = 'jonnyzoom-modal-image'; 
        frame.appendChild(clone); 
        wrapper.appendChild(frame);
        modal.classList.add('is-active'); 
        modal.setAttribute('aria-hidden','false'); 
        overlay.setAttribute('aria-hidden','true'); 
        scrollLock.lock();

        const { w: finalW_thumb, h: finalH_thumb } = computeFinalSizeForImage(thumbImg);
        const hScaled = Math.round(thumbRect.height * (finalW_thumb / thumbRect.width));
        frame.style.width  = finalW_thumb + 'px'; frame.style.height = hScaled + 'px'; frame.getBoundingClientRect();
        const { dx, dy } = computeTranslateDelta(thumbRect); const scale = thumbRect.width / finalW_thumb;
        frame.style.transition = 'none'; frame.style.transform  = `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${scale})`; frame.getBoundingClientRect(); frame.style.transition = '';
        requestAnimationFrame(() => { frame.style.transform = 'translate(-50%, -50%) translate(0, 0) scale(1)'; });

        const onZoomInEnd = async (ev) => {
          if (ev.propertyName !== 'transform') return;
          overlay.classList.add('is-active'); 
          overlay.setAttribute('aria-hidden','false');
          if (supportsNativeAuto) {
            frame.style.height = 'auto'; 
          } else {
            frame.style.height = finalH_thumb + 'px';
          }
          const dur = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--duration-size'));
          await waitForTransition(frame, 'height', isFinite(dur) ? Math.max(400, 1.2 * dur) : 900);
          if (mediaType === 'video' || mediaType === 'youtube') {
            const { w: vW, h: vH } = computeFinalSizeForAspect(16, 9);
            frame.classList.add('is-morphing'); frame.style.width = vW + 'px'; frame.style.height = vH + 'px';
            await Promise.all([ waitForTransition(frame, 'width', 500), waitForTransition(frame, 'height', 500) ]);
            frame.classList.remove('is-morphing');
          }
          if (mediaType === 'image') { const hi = await hiResPromise; if (hi) clone.src = highResUrl; }
          else if (mediaType === 'video') { const videoEl = buildHTML5Video(aEl, { autoplay: config.autoplay }); if (videoEl) { frame.appendChild(videoEl); videoEl.getBoundingClientRect(); requestAnimationFrame(async () => { clone.style.opacity = '0'; clone.style.pointerEvents = 'none'; videoEl.style.opacity = '1'; videoEl.style.pointerEvents = ''; if (videoEl._shouldAutoplay) { try { await videoEl.play(); } catch { videoEl.muted = true; try { await videoEl.play(); } catch {} } } }); active.mediaEl = videoEl; } }
          else { const ifr = buildYouTubeIframe(aEl, { autoplay: config.autoplay }); if (ifr) { frame.appendChild(ifr); ifr.getBoundingClientRect(); requestAnimationFrame(() => { clone.style.opacity = '0'; clone.style.pointerEvents = 'none'; ifr.style.opacity = '1'; ifr.style.pointerEvents = ''; }); active.mediaEl = ifr; } }
          frame.removeEventListener('transitionend', onZoomInEnd);
          ensureActiveMediaVisibility();

          renderThumbs();
        };
        frame.addEventListener('transitionend', onZoomInEnd);

        active = { aEl, sourceImg: thumbImg, frameEl: frame, imgEl: clone, finalW: finalW_thumb, finalH: finalH_thumb, hScaled, supportsNativeAuto, group, groupEls, groupIndex, captionText, mediaType, mediaEl: (mediaType === 'image' ? clone : null) };
        attachSwipe(frame); updateNavState();
      }

      async function showAtIndex(index){
        if (!active) return;
        const { groupEls, frameEl, imgEl } = active;
        const aNew = groupEls[index]; if (!aNew) return;

        const thumbNew = aNew.querySelector('img');
        const captionText = getCaptionForAnchor(aNew, thumbNew);
        applyCaption(captionText);

        const mediaType = getMediaType(aNew);

        // v3.4.1c — if new item is an image with NO thumb, preload href to get intrinsic size
        let nextNode, sizeTarget;
        if (mediaType === 'image' && !thumbNew) {
          const href = (aNew.getAttribute('href') || '').trim();
          if (isImageHref(href)) {
            let preImg;
            try { preImg = await preloadImage(href); } catch { preImg = new Image(); preImg.src = href; }
            preImg.className = 'jonnyzoom-modal-image';
            preImg.style.position = 'absolute'; preImg.style.inset = '0'; preImg.style.opacity = '0'; preImg.style.zIndex = '1';
            sizeTarget = computeFinalSizeFromIntrinsic(preImg.naturalWidth || 1200, preImg.naturalHeight || 800);
            frameEl.classList.add('is-morphing'); frameEl.style.width = sizeTarget.w + 'px'; frameEl.style.height = sizeTarget.h + 'px';
            await Promise.all([ waitForTransition(frameEl, 'width', 500), waitForTransition(frameEl, 'height', 500) ]);
            frameEl.appendChild(preImg);
            nextNode = preImg;
          } else {
            // not a valid image href → bail gracefully
            return;
          }
        } else {
          // existing logic for items with a thumb (or non-image kinds)
          const computedTarget = (mediaType === 'image')
            ? computeFinalSizeForImage(thumbNew)
            : (mediaType === 'html' ? computeFinalSizeForHtml(aNew) : computeFinalSizeForAspect(16, 9));

          sizeTarget = computedTarget;
          frameEl.classList.add('is-morphing'); frameEl.style.width  = sizeTarget.w + 'px'; frameEl.style.height = sizeTarget.h + 'px';
          await Promise.all([ waitForTransition(frameEl, 'width', 500), waitForTransition(frameEl, 'height', 500) ]);

          if (mediaType === 'image') {
            const highResUrl = aNew.getAttribute('href') || (thumbNew && (thumbNew.currentSrc || thumbNew.src));
            const hiResPromise = (highResUrl && imgEl && highResUrl !== imgEl.src) ? preloadImage(highResUrl).catch(() => null) : Promise.resolve(null);
            const nextImg = thumbNew.cloneNode(true); nextImg.className = 'jonnyzoom-modal-image'; nextImg.style.position = 'absolute'; nextImg.style.inset = '0'; nextImg.style.opacity = '0'; nextImg.style.zIndex = '1'; frameEl.appendChild(nextImg);
            const hi = await hiResPromise; if (hi) nextImg.src = highResUrl; nextNode = nextImg;
          } else if (mediaType === 'video') { nextNode = buildHTML5Video(aNew, { autoplay: config.autoplay }); if (nextNode) frameEl.appendChild(nextNode); }
          else if (mediaType === 'youtube') { nextNode = buildYouTubeIframe(aNew, { autoplay: config.autoplay }); if (nextNode) frameEl.appendChild(nextNode); }
          else /* html */ { nextNode = buildHtmlContent(aNew); if (nextNode) { nextNode.style.opacity = '0'; frameEl.appendChild(nextNode); } }
        }

        if (nextNode) {
          nextNode.getBoundingClientRect();
          requestAnimationFrame(() => { if (imgEl) imgEl.style.opacity = '0'; nextNode.style.opacity = '1'; });
          await waitForTransition(nextNode, 'opacity', 500);
          if (imgEl && imgEl !== nextNode) imgEl.remove();
          if (nextNode.style) { nextNode.style.position = ''; nextNode.style.inset = ''; nextNode.style.zIndex = ''; }
          frameEl.classList.remove('is-morphing');
          const visibleIsMedia = nextNode.classList.contains('jonnyzoom-modal-media');
          if (visibleIsMedia) {
            const posterClone = thumbNew ? thumbNew.cloneNode(true) : null;
            if (posterClone) { posterClone.className = 'jonnyzoom-modal-image'; posterClone.style.opacity = '0'; posterClone.style.pointerEvents = ''; frameEl.appendChild(posterClone); active.imgEl = posterClone; }
          } else {
            active.imgEl = nextNode;
          }
          active.mediaEl = nextNode;
          if (nextNode.tagName === 'VIDEO' && nextNode._shouldAutoplay) { try { await nextNode.play(); } catch { nextNode.muted = true; try { await nextNode.play(); } catch {} } }
        }
        active.aEl = aNew; active.sourceImg = thumbNew || null; 
        active.groupIndex = index; 
        active.finalW = sizeTarget.w; 
        active.finalH = sizeTarget.h; 
        active.mediaType = mediaType;
        updateThumbActive(index); 
        ensureActiveMediaVisibility();
      }

      async function navigate(delta){
        if (!active || !active.groupEls || active.groupEls.length < 2) return; 
        if (navBusy) return; 
        navBusy = true;
        const count = active.groupEls.length; 
        const wrap = isWrapMode();
        let nextIndex = active.groupIndex + delta; 
        if (wrap) {
          nextIndex = (nextIndex + count) % count; 
        } else { 
          if (nextIndex < 0 || nextIndex >= count) { 
            navBusy = false; return; 
          } 
        }
        await showAtIndex(nextIndex); 
        updateNavState(); 
        navBusy = false;
      }

      function stopAndCleanupMedia(frameEl){
        const vids = frameEl.querySelectorAll('video.jonnyzoom-modal-media'); vids.forEach(v => { try { v.pause(); v.currentTime = 0; } catch(_){} });
        const ifr = frameEl.querySelectorAll('iframe.jonnyzoom-modal-media'); ifr.forEach(el => { try { el.src = el.src; } catch(_){} });
      }

      // Helper: safe rect with fallback center point
      function safeRect(el){
        try { if (el && typeof el.getBoundingClientRect === 'function') {
          const r = el.getBoundingClientRect();
          if (r && isFinite(r.width) && isFinite(r.height)) return r;
        } } catch(_) {}
        // Fallback: pretend it's a 1px rect at viewport center
        return {
          width: 1, height: 1,
          left: (window.innerWidth/2) - 0.5,
          top:  (window.innerHeight/2) - 0.5
        };
      }

      // ---- helpers (place with other helpers) ----
      function getOriginRect(sourceImg, aEl){
        // Prefer the img rect; if 0-sized, try the anchor; else fallback center 1px.
        const isValid = (r) => r && isFinite(r.width) && isFinite(r.height) && r.width > 0.5 && r.height > 0.5;
        try {
          if (sourceImg && typeof sourceImg.getBoundingClientRect === 'function') {
            const r = sourceImg.getBoundingClientRect();
            if (isValid(r)) return r;
          }
          if (aEl && typeof aEl.getBoundingClientRect === 'function') {
            const r2 = aEl.getBoundingClientRect();
            if (isValid(r2)) return r2;
          }
        } catch(_) {}
        // fallback: “fake” rect at viewport center so we can still close
        return {
          width: 1, height: 1,
          left: (window.innerWidth/2) - 0.5,
          top:  (window.innerHeight/2) - 0.5
        };
      }

      async function waitPropOrTimeout(el, propName, ms){
        // Races transitionend(propName) vs timeout
        return Promise.race([
          waitForTransition(el, propName, ms),
          new Promise(r => setTimeout(r, ms))
        ]);
      }

      // ---- replace your existing closeToOrigin with this ----
      async function closeToOrigin(){
        if (!active || active._closing) return;
        active._closing = true;

        const { sourceImg, frameEl, supportsNativeAuto, aEl } = active;

        // Hide UI immediately
        navWrap.classList.remove('is-visible'); 
        navWrap.setAttribute('aria-hidden','true');
        btnPrev.classList.remove('is-disabled'); 
        btnNext.classList.remove('is-disabled');
        btnPrev.removeAttribute('aria-disabled'); 
        btnNext.removeAttribute('aria-disabled');
        captionEl.classList.remove('is-visible');
        overlay.classList.remove('is-active'); 
        overlay.setAttribute('aria-hidden','true');
        frameEl.classList.remove('is-morphing');
        hideThumbs();
        stopAndCleanupMedia(frameEl);
        ensureActiveMediaVisibility();

        setTimeout(function(){
          //btnClose.style.display = 'none';
          document.querySelector('.jonnyzoom-modal .jonnyzoom-modal-close').style.display = 'none';
        },400);        

        // HTML mode or no sourceImg → deterministic fade/scale close (no reverse morph)
        if (active.mediaType === 'html' || !sourceImg) {
          // kick a tiny scale and tear down regardless of events
          frameEl.style.transition = frameEl.style.transition || ''; // ensure transitions apply
          frameEl.getBoundingClientRect();
          frameEl.style.transform = 'translate(-50%, -50%) scale(0.96)';
          await waitPropOrTimeout(frameEl, 'transform', 700);
          modal.classList.remove('is-active'); modal.setAttribute('aria-hidden','true');
          scrollLock.unlock();
          if (frameEl.parentNode === wrapper) wrapper.removeChild(frameEl);
          active = null; 
          navBusy = false;
          destroyModalScaffold();
          return;
        }

        // IMAGE / VIDEO / YT reverse morph
        // 1) Determine a safe origin rect (survives width:auto/height:auto)
        const rect = getOriginRect(sourceImg, aEl);
        const rectHasSize = rect.width > 0.5 && rect.height > 0.5;

        if (!rectHasSize) {
          // Can’t compute a meaningful morph → fall back to fade/scale close
          frameEl.getBoundingClientRect();
          frameEl.style.transform = 'translate(-50%, -50%) scale(0.96)';
          await waitPropOrTimeout(frameEl, 'transform', 700);
          modal.classList.remove('is-active'); 
          modal.setAttribute('aria-hidden','true');
          scrollLock.unlock();
          if (frameEl.parentNode === wrapper) wrapper.removeChild(frameEl);
          active = null; navBusy = false; 
          destroyModalScaffold();
          return;
        }

        // 2) Recompute final target size from intrinsic data (deterministic)
        const disp = active.imgEl || sourceImg;
        const natW = (disp && disp.naturalWidth)  || (sourceImg && sourceImg.naturalWidth)  || 1200;
        const natH = (disp && disp.naturalHeight) || (sourceImg && sourceImg.naturalHeight) || 800;
        const final = computeFinalSizeFromIntrinsic(natW, natH);
        active.finalW = final.w; active.finalH = final.h;

        // 3) Animate height back to the “uniform scaled from thumb” height
        const safeDiv = final.w > 0 ? (final.w) : 1;
        const scaleFromThumb = rect.width / safeDiv; // <= guarded
        const hScaled = Math.max(1, Math.round(rect.height / (scaleFromThumb || 1)));
        active.hScaled = hScaled;

        frameEl.style.width = final.w + 'px';
        frameEl.style.height = supportsNativeAuto ? 'auto' : final.h + 'px';
        frameEl.getBoundingClientRect(); // commit
        frameEl.style.height = hScaled + 'px';

        await waitPropOrTimeout(frameEl, 'height', 700);

        // 4) Reverse FLIP back to the origin
        const { dx, dy } = computeTranslateDelta(rect);
        const backScale = final.w > 0 ? (rect.width / final.w) : 1;

        // Ensure we start from identity center before animating out
        frameEl.getBoundingClientRect();
        frameEl.style.transform = `translate(-50%, -50%) translate(${dx}px, ${dy}px) scale(${backScale})`;

        await waitPropOrTimeout(frameEl, 'transform', 700);

        // 5) Tear down (no dependence on any further events)
        modal.classList.remove('is-active'); modal.setAttribute('aria-hidden','true');
        scrollLock.unlock();
        if (frameEl.parentNode === wrapper) wrapper.removeChild(frameEl);
        active = null; 
        navBusy = false;   
        destroyModalScaffold();
      }

      function attachSwipe(frameEl){
        let swipeId = null; let startX = 0, startY = 0; let moved = false; let samples = []; const TIME_WINDOW_MS = 110;
        const PX_ACCEPT = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--swipe-accept')) || 60;
        const V_THRESH  = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--swipe-vel-threshold')) || 0.45;
        function canNavigate(dir){ if (!active || !active.groupEls) return false; if (isWrapMode()) return true; const atStart = active.groupIndex === 0; const atEnd = active.groupIndex === active.groupEls.length - 1; if (dir < 0 && atStart) return false; if (dir > 0 && atEnd) return false; return true; }
        function onDown(e){ if (navBusy) return; if (e.button !== undefined && e.button !== 0) return; swipeId = e.pointerId; startX = e.clientX; startY = e.clientY; moved = false; samples = [{ x: startX, t: performance.now() }]; frameEl.setPointerCapture?.(swipeId); }
        function onMove(e){ if (swipeId === null || e.pointerId !== swipeId) return; const now = performance.now(); const dx = e.clientX - startX; const dy = e.clientY - startY; if (!moved && Math.hypot(dx, dy) >= 6) moved = true; samples.push({ x: e.clientX, t: now }); if (samples.length > 8) samples.shift(); while (samples.length > 1 && now - samples[0].t > TIME_WINDOW_MS) samples.shift(); tapGuardUntil = Date.now() + 350; }
        function computeVelocity(){ if (samples.length < 2) return 0; const last = samples[samples.length - 1]; let ref = samples[0]; for (let i = samples.length - 2; i >= 0; i--){ if (last.t - samples[i].t >= 60) { ref = samples[i]; break; } } const dt = last.t - ref.t; if (dt <= 0) return 0; return (last.x - ref.x) / dt; }
        function onEnd(e){ if (swipeId === null || e.pointerId !== swipeId) return; frameEl.releasePointerCapture?.(swipeId); const dx = e.clientX - startX; const adx = Math.abs(dx); const vel = computeVelocity(); const vabs = Math.abs(vel); const dirByDx = dx > 0 ? -1 : 1; const dirByVel = vel > 0 ? -1 : 1; const triggerByDistance = moved && adx >= PX_ACCEPT; const triggerByVelocity = moved && vabs >= V_THRESH && adx >= 10; const dir = triggerByVelocity ? dirByVel : dirByDx; if ((triggerByDistance || triggerByVelocity) && canNavigate(dir)){ navigate(dir); } swipeId = null; }
        frameEl.addEventListener('pointerdown', onDown);
        frameEl.addEventListener('pointermove', onMove);
        frameEl.addEventListener('pointerup', onEnd);
        frameEl.addEventListener('pointercancel', onEnd);
        //frameEl.addEventListener('click', (e) => { 
        //  if (Date.now() < tapGuardUntil) e.stopPropagation(); 
        //});
        frameEl.addEventListener('click', (e) => {
          if (Date.now() < tapGuardUntil) {
            const interactive = e.target.closest('a,button,input,textarea,select,label,[role="button"],[onclick]');
            if (!interactive) e.stopPropagation(); // let real controls bubble
          }
       });
      }

      // ------- Events & public API -------
      const onGalleryClick = (e) => {

        // v3.4.1c — RESTRICT: only handle clicks that originate within a configured trigger
        const anchor = e.target.closest(config.triggerSelector);
        if (!anchor) return;
        
        e.preventDefault();

        // HTML panel
        if (isHashHref(anchor)) { openHtmlFromAnchor(anchor); return; }

        // If the anchor has a child <img>, use standard zoom-from-origin
        const img = anchor.querySelector('img');

        if (img) {
          openFromOrigin(anchor, img);
          return;
        }

        // v3.4.1c — NEW: fallback to image-from-href if href looks like an image URL
        const href = (anchor.getAttribute('href') || '').trim();
        if (isImageHref(href)) {
          openImageHrefNoThumb(anchor);
          return;
        }

        // Else: video/youtube paths will be handled when they have thumbs;
        // if none, we ignore with a gentle warn.
        //if (window.console) console.warn('Gallery click ignored: no <img> and href is not an image/hash', anchor);
      };
      gallery.addEventListener('click', onGalleryClick);

      const api = {
        destroy() {
          if (active) closeToOrigin();
          gallery.removeEventListener('click', onGalleryClick);
        },
        open(target) {
          let el = null;

          // Number → index within configured triggers
          if (typeof target === 'number') {
            let items;
            // v3.4.1c — honor optional parentSelector first
            if (config.parentSelector) {
              const parent = document.querySelector(config.parentSelector);
              items = parent ? parent.querySelectorAll(config.triggerSelector) : [];
            } else {
              // if a gallery root exists use it; else scan document
              items = (gallery || document).querySelectorAll(config.triggerSelector);
            }

            if (target >= 0 && target < items.length) {
              el = items[target];
            } else {
              //if (window.console) console.warn(`open(): index ${target} out of range`);
              return;
            }
          }

          // String → selector (query within gallery if present, else document)
          if (typeof target === 'string') {
            el = (gallery || document).querySelector(target);
          }

          // Element → use directly
          if (target instanceof Element) {
            el = target;
          }

          if (!el) {
            //if (window.console) console.warn('open(): target not found in gallery', target);
            return;
          }

          // Anchor
          if (el.tagName === 'A') {
            if (isHashHref(el)) { openHtmlFromAnchor(el); return; }
            const img = el.querySelector('img');
            if (img) { openFromOrigin(el, img); return; }

            // v3.4.1c — NEW: <a> without <img> but image href
            const href = (el.getAttribute('href') || '').trim();
            if (isImageHref(href)) { openImageHrefNoThumb(el); return; }

            //if (window.console) console.warn('open(): no <img> found and href not an image', el);
            return;
          }

          // IMG → find closest configured trigger; else ignore
          if (el.tagName === 'IMG') {
            const anchor = el.closest(config.triggerSelector);
            if (anchor) { openFromOrigin(anchor, el); return; }
            //if (window.console) console.warn('open(): IMG is not inside a trigger', el);
            return;
          }

          //if (window.console) console.warn('open(): unsupported target element', el);
        }
      };

      return api;
    });
  }

  // Expose the factory globally
  global.createZoomFromOriginGallery = createZoomFromOriginGallery;

  /**
   * Helper that initializes a gallery and (optionally) exposes it on window.
   * @param {string|Element|Object} rootSelector
   * @param {Object} [userConfig]
   * @param {string} [globalName]
   * @returns {Promise<Object>} gallery instance
   */
  function jonnyZoom(rootSelector, userConfig = {}, globalName = null) {

    // v3.4.1c — PATCH: allow config-only call in the helper too
    // jonnyZoom({ triggerSelector: '.foo' }, 'MyGlobal')
    if (rootSelector && typeof rootSelector === 'object' && !(rootSelector instanceof Element)) {
      if (typeof userConfig === 'string') {
        globalName = userConfig;
      }
      userConfig = rootSelector;
      rootSelector = document;
    }

    const p = createZoomFromOriginGallery(rootSelector, userConfig)
      .then((galleryInstance) => {
        if (globalName && typeof window !== 'undefined') {
          //if (window[globalName] && window[globalName] !== galleryInstance) {
            //if(window.console){ console.warn(`window.${globalName} already exists – overwriting.`); }
          //}
          window[globalName] = Object.freeze(galleryInstance);
          //if(window.console){
          //  console.log(`Gallery '${globalName}' initialized and is available as window.${globalName}`);
          //}
        }
        return galleryInstance;
      })
      .catch((error) => {
        if(window.console){
          console.error(`Failed to initialize gallery for selector: ${rootSelector}`, error);
        }
        throw error;
      });
    return p;
  }

  // Expose the helper globally
  global.jonnyZoom = jonnyZoom;

})(typeof window !== 'undefined' ? window : this);
